import { Injectable, inject } from '@angular/core';
import { APP_NAME } from '@freelancer/config';
import { Deposits } from '@freelancer/deposits';
import {
  PaymentsErrorType,
  PaymentsMessagingService,
  PaymentsResultStatus,
} from '@freelancer/payments-messaging';
import { ErrorTracking } from '@freelancer/tracking';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DepositMethodApi } from 'api-typings/common/common';
import type { DepositRequestApi } from 'api-typings/payments/payments';
import { firstValueFrom } from 'rxjs';
import { APPLE_PAY_CONFIG } from './apple-pay.config';
import { ApplePayConfig } from './apple-pay.interface';
import type {
  ApplePayDepositRequest,
  ApplePayPaymentCredentialStatus,
  ApplePayPaymentRequest,
} from './apple-pay.model';

/**
 * Service to handle Apple Pay payments based on the Apple Pay JS API
 */
@UntilDestroy({ className: 'ApplePay' })
@Injectable({
  providedIn: 'root',
})
export class ApplePay {
  APPLE_PAY_VERSION = 3;
  private applePayConfig = inject<ApplePayConfig>(APPLE_PAY_CONFIG);
  private appName = inject(APP_NAME);
  private deposits = inject(Deposits);
  private errorTracking = inject(ErrorTracking);
  private paymentsMessaging = inject(PaymentsMessagingService);
  private activeSession: ApplePaySession | undefined;
  private sdkLoadPromise: Promise<void> | undefined;

  private loadSDK(): Promise<void> {
    if (this.sdkLoadPromise !== undefined) {
      return this.sdkLoadPromise;
    }
    this.sdkLoadPromise = new Promise<void>((resolve, reject) => {
      const element = document.createElement('script');
      element.src =
        'https://applepay.cdn-apple.com/jsapi/v1.3.6/apple-pay-sdk.js';
      element.integrity =
        'sha384-7KJIkGT+8p0K2rhsEQcz7zZ+nYUFUbN573ZKSgwp9YKN7uUC+h5TAhEIdOAZgo6R';
      element.crossOrigin = 'anonymous';
      element.onload = () => resolve();
      element.onerror = () => {
        this.errorTracking.captureMessage('Apple Pay SDK failed to load');
        this.sdkLoadPromise = undefined;
        reject(new Error('Apple Pay SDK failed to load'));
      };
      document.head.appendChild(element);
    });
    return this.sdkLoadPromise;
  }

  /**
   * Whether the Apple Pay Session API is available and supports our version.
   */
  private isApplePaySupported(): boolean {
    try {
      // Throws when trying to start an Apple Pay session from an insecure document.
      return !!window.ApplePaySession?.supportsVersion(this.APPLE_PAY_VERSION);
    } catch {
      return false;
    }
  }

  /**
   * Check if the current device supports Apple Pay
   */
  async canMakePayments(): Promise<boolean> {
    try {
      await this.loadSDK();
    } catch {
      return false;
    }
    return this.isApplePaySupported();
  }

  /**
   * Check if the current device supports Apple Pay and have an active card in the wallet
   */
  async canMakePaymentWithActiveCard(): Promise<boolean> {
    try {
      await this.loadSDK();
    } catch {
      return false;
    }

    if (!this.isApplePaySupported()) {
      return false;
    }

    try {
      const capabilities = await (window.ApplePaySession.applePayCapabilities(
        this.applePayConfig.merchantIdentifier,
      ) as Promise<{
        paymentCredentialStatus: ApplePayPaymentCredentialStatus;
      }>);

      return (
        capabilities.paymentCredentialStatus === 'paymentCredentialsAvailable'
      );
    } catch {
      return false;
    }
  }

  /**
   * Make an Apple Pay payment
   */
  async makePayment(request: ApplePayDepositRequest): Promise<void> {
    try {
      await this.loadSDK();
    } catch {
      throw new Error('Apple Pay SDK failed to load');
    }
    if (!this.isApplePaySupported()) {
      throw new Error('Apple Pay is not supported');
    }

    const applePayRequest: ApplePayPaymentRequest = {
      countryCode: request.countryCode,
      currencyCode: request.currency.code,
      supportedNetworks: ['visa', 'masterCard', 'amex', 'discover'],
      merchantCapabilities: ['supports3DS'],
      total: {
        label:
          this.appName === 'freightlancer' ? 'Loadshift' : 'Freelancer.com',
        amount: request.totalAmount.toString(),
      },
    };

    this.cancelPayment();

    const session = new window.ApplePaySession(
      this.APPLE_PAY_VERSION,
      applePayRequest,
    );
    this.activeSession = session;

    let invoiceId: number | undefined;

    /**
     * Requests a merchant session from the backend and completes the merchant validation.
     */
    session.onvalidatemerchant = async (
      event: ApplePayJS.ApplePayValidateMerchantEvent,
    ) => {
      const payload: DepositRequestApi = {
        amount: request.depositAmount,
        currency_id: request.currency.id,
        country_code: request.countryCode,
        deposit_method: DepositMethodApi.APPLEPAY,
        user_id: request.userId,
        cart_id: request.cartId,
        confirm_url: event.validationURL,
        threatmetrix_session: request.threatmetrixSession,
        back_url: request.backUrl,
        tracking_token: request.trackingToken,
        cancel_url: request.cancelUrl,
        action: request.action,
        domain: request.domain,
      };
      const response = await firstValueFrom(
        this.deposits.deposit(payload).pipe(untilDestroyed(this)),
      );
      if (response.status === 'success') {
        invoiceId = response.result.invoice?.id;
        const merchantSession = {
          epochTimestamp: response.result.apple_pay_session?.epoch_timestamp,
          expiresAt: response.result.apple_pay_session?.expires_at,
          merchantSessionIdentifier:
            response.result.apple_pay_session?.merchant_session_identifier,
          nonce: response.result.apple_pay_session?.nonce,
          merchantIdentifier:
            response.result.apple_pay_session?.merchant_identifier,
          domainName: response.result.apple_pay_session?.domain_name,
          displayName: response.result.apple_pay_session?.display_name,
          signature: response.result.apple_pay_session?.signature,
        };
        session.completeMerchantValidation(merchantSession);
      } else {
        this.cancelPayment();
        this.paymentsMessaging.pushError({
          errorCode: response.errorCode,
          errorType: PaymentsErrorType.ERROR,
          eventId: response.requestId,
        });
      }
    };

    /**
     * Payment has been authorized. Complete the payment by sending the token.
     */
    session.onpaymentauthorized = async (
      event: ApplePayJS.ApplePayPaymentAuthorizedEvent,
    ) => {
      const payload = {
        user_id: request.userId,
        deposit_method: DepositMethodApi.APPLEPAY,
        paypal_token: btoa(JSON.stringify(event.payment.token.paymentData)),
        invoice_id: invoiceId,
        action: request.action,
      };

      const response = await this.deposits.depositUpdate(payload);
      this.activeSession = undefined;

      if (response.status === 'success') {
        session.completePayment(window.ApplePaySession.STATUS_SUCCESS);
        this.paymentsMessaging.pushMessage({
          paymentsStatus: PaymentsResultStatus.SUCCESS,
          method: DepositMethodApi.APPLEPAY,
        });
      } else {
        session.completePayment(window.ApplePaySession.STATUS_FAILURE);
        this.paymentsMessaging.pushError({
          errorCode: response.errorCode,
          errorType: PaymentsErrorType.ERROR,
          eventId: response.requestId,
        });
      }
    };

    /**
     * User has cancelled the payment in the Apple Pay payment sheet.
     */
    session.oncancel = () => {
      this.activeSession = undefined;
      this.paymentsMessaging.pushMessage({
        paymentsStatus: PaymentsResultStatus.INTERRUPTED,
        method: DepositMethodApi.APPLEPAY,
      });
    };

    /**
     * Begin the Apple Pay transaction.
     */
    session.begin();
  }

  /**
   * Abort any active Apple Pay session, which also dismisses the payment sheet.
   * Call this when navigating away from the deposit page.
   */
  cancelPayment(): void {
    if (this.activeSession) {
      try {
        this.activeSession.abort();
      } catch {
        // Session may already be in a terminal state (completed/cancelled)
      }
      this.activeSession = undefined;
    }
  }
}
